import { useRef, useState, useCallback, useEffect } from "react";
import Webcam from "react-webcam";
import useEmployeeDetails from "../hooks/useEmployeeDetails";
import { useGeolocated } from "react-geolocated";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";
import { IoMdCloseCircleOutline } from "react-icons/io";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const WebcamCapture = ({ mode = "checkin", onClose }) => {
  const { id, employeeToken } = useEmployeeDetails();
  const API_URL = process.env.REACT_APP_BASE_URL;

  const { coords, isGeolocationAvailable, isGeolocationEnabled } = useGeolocated({
    positionOptions: { enableHighAccuracy: true },
    userDecisionTimeout: 10000,
  });

  const [loading, setLoading] = useState(false);
  const webcamRef = useRef(null);
  const [imageSrc, setImageSrc] = useState(null);

  const getPosition = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) reject("Geolocation not supported");
      navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true });
    });

  // Inside WebcamCapture, after useState declarations
  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const position = await getPosition();
        console.log("Initial coords:", position.coords);
        // optionally store it if needed
        // setCoords({ latitude: position.coords.latitude, longitude: position.coords.longitude });
      } catch (err) {
        toast.error("Enable location services to check in.");
      }
    };

    fetchLocation();
  }, []);


  // Show toast if location not available
  useEffect(() => {
    if (!isGeolocationAvailable) {
      toast.error("Geolocation is not supported by your browser.");
    } else if (!isGeolocationEnabled) {
      toast.error("Geolocation is disabled. Please enable location services.");
    } else if (!coords) {
      toast.info("Fetching location, please wait...");
    }
  }, [coords, isGeolocationAvailable, isGeolocationEnabled]);

  function base64ToBlob(base64Data, contentType = "image/jpeg") {
    const byteCharacters = atob(base64Data.split(",")[1]);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      byteArrays.push(new Uint8Array(byteNumbers));
    }
    return new Blob(byteArrays, { type: contentType });
  }

  const handleSubmit = useCallback(async () => {
    setLoading(true);

    const validateInputs = () => {
      if (!imageSrc) {
        toast.error("Please capture an image first!");
        setLoading(false);
        return false;
      }
      if (!id) {
        toast.error("Employee ID is missing!");
        setLoading(false);
        return false;
      }
      if (!employeeToken) {
        toast.error("Authorization token missing. Please login again.");
        setLoading(false);
        return false;
      }
      if (!coords?.latitude || !coords?.longitude) {
        toast.error("Location not available!");
        setLoading(false);
        return false;
      }
      return true;
    };

    if (!validateInputs()) return;

    try {
      const imageBlob = base64ToBlob(imageSrc);
      const imageFile = new File([imageBlob], "checkin.jpg", { type: "image/jpeg" });

      const formData = new FormData();
      formData.append("image", imageFile);
      formData.append("latitude", coords.latitude);
      formData.append("longitude", coords.longitude);
      formData.append("id", id);

      const response = await axios.post(`${API_URL}/staff_toggle_checkin`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${employeeToken}`,
        },
      });

      if (response.data?.success === true) {
        toast.success(`${mode === "checkin" ? "Check-In" : "Check-Out"} successful!`);
        setTimeout(() => window.location.reload(), 1500);
      } else {
        toast.error(response.data?.error || `${mode === "checkin" ? "Check-In" : "Check-Out"} failed.`);
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.error || "Error sending data.");
    } finally {
      setLoading(false);
    }
  }, [imageSrc, coords, id, employeeToken, API_URL, mode]);

  const capture = () => {
    const img = webcamRef.current.getScreenshot();
    setImageSrc(img);
  };

  const removeImage = () => setImageSrc("");

  // Validation conditions for submit
  const isSubmitDisabled =
    loading ||
    !imageSrc ||
    !id ||
    !employeeToken ||
    !coords?.latitude ||
    !coords?.longitude;

  return (
    <div className="relative flex flex-col items-center justify-center min-h-full px-2">
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute -top-0 -right-2 text-red-600 hover:text-red-800 z-50"
      >
        <IoMdCloseCircleOutline size={36} />
      </button>

      <div className={`${loading ? "blur-sm pointer-events-none" : ""} w-full`}>
        {imageSrc ? (
          <div className="flex flex-col items-center justify-center">
            <img
              src={imageSrc}
              alt="Captured"
              className="w-80 h-auto rounded border border-gray-300 shadow"
            />
            <div className="flex flex-row items-center gap-6 justify-around">
              <button
                onClick={removeImage}
                className="mt-4 px-6 py-2 bg-red-700 text-white rounded hover:bg-red-800"
              >
                Retake
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitDisabled}
                className={`mt-4 px-6 py-2 rounded text-white ${isSubmitDisabled
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-green-600 hover:bg-green-700"
                  }`}
              >
                Submit
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center py-4">
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              className="rounded-lg max-w-full"
              videoConstraints={{ facingMode: "user" }}
              disabled={loading}
            />
            <button
              onClick={capture}
              className="mt-2 px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              Capture Photo
            </button>
          </div>
        )}
      </div>

      {loading && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <AiOutlineLoading3Quarters className="animate-spin text-white text-6xl" />
        </div>
      )}

      <ToastContainer position="top-center" />
    </div>
  );
};

export default WebcamCapture;
